import { InputOTP6 } from "@/components/molecules/otp";
import { PhoneInput } from "@/components/molecules/phone";
import { getProbableCountryCodeFromBrowser } from "@/components/molecules/phone/utils";
import { Button } from "@/components/ui/button";
import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { usePhoneChallengeFlow } from "@/features/auth/hooks/use-phone-challenge";
import { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { formatPhoneNumberIntl } from "react-phone-number-input";

export const PhoneChallenge = (props: {
  enforcedValue?: {
    label: string; // Ex: "+33 6 ** ** ** 42"
    id: string; // Used by the backend to find the correct phone number
  };
  /**
   * A number to start from, still editable: the one on the account that no
   * code has reached yet. Unlike `enforcedValue`, nothing is sent until the
   * user asks.
   */
  initialPhone?: string;
  onSuccess: (preSignedToken: string) => void;
}) => {
  const { t } = useTranslation();
  const {
    phone,
    setPhone,
    sendChallenge,
    codeSent,
    verifyCode,
    isVerifyingCode,
    retryTimeLeft,
    canRetry,
    retry,
  } = usePhoneChallengeFlow({
    enforcedValue: props.enforcedValue,
    initialPhone: props.initialPhone,
    onSuccess: props.onSuccess,
    retryDelaySeconds: 60,
  });
  const [isLoading, setIsLoading] = useState(false);

  return (
    <>
      <form onSubmit={(e) => e.preventDefault()}>
        <CardHeader>
          {!codeSent && (
            <CardDescription>{t("phone.enterPhone")}</CardDescription>
          )}
          {codeSent && (
            <CardDescription>
              <Trans
                i18nKey="phone.codeSent"
                values={{
                  phone:
                    props.enforcedValue?.label || formatPhoneNumberIntl(phone),
                }}
                components={{ strong: <strong /> }}
              />
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-6">
            {!codeSent && (
              <div className="grid gap-2">
                <Label>{t("phone.label")}</Label>
                <PhoneInput
                  required
                  placeholder={t("phone.placeholder")}
                  defaultCountry={getProbableCountryCodeFromBrowser()}
                  value={phone}
                  onChange={(value) => setPhone(value || "")}
                  disabled={isLoading}
                />
              </div>
            )}
            {codeSent && (
              <>
                <div className="grid gap-2">
                  <Label>{t("otp.label")}</Label>
                  <InputOTP6
                    required
                    onChange={(value) => {
                      if (value.length === 6) {
                        verifyCode(value);
                      }
                    }}
                    disabled={isLoading}
                  />
                </div>
              </>
            )}
          </div>
        </CardContent>
        <CardFooter className="gap-2">
          {!codeSent && (
            <Button
              type="submit"
              className="w-full"
              onClick={async () => {
                setIsLoading(true);
                try {
                  await sendChallenge();
                } finally {
                  setIsLoading(false);
                }
              }}
              disabled={!phone.length || isLoading || isVerifyingCode}
            >
              {isLoading ? t("actions.loading") : t("actions.sendCode")}
            </Button>
          )}

          {codeSent && (
            <Button
              type="button"
              variant="link"
              className="p-0"
              onClick={retry}
              disabled={!canRetry || isLoading || isVerifyingCode}
            >
              {canRetry
                ? t("actions.resendCode")
                : t("actions.retryIn", { seconds: retryTimeLeft })}
            </Button>
          )}
        </CardFooter>
      </form>
    </>
  );
};
